// src/components/layout/RootLayout.tsx
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useTourStore } from "@/store/tour-store";
import { Button } from "../ui/button";
import { HelpCircle } from "lucide-react";

const getPageTitle = (pathname: string) => {
  if (pathname.startsWith('/summary/')) {
    return 'Candidate Profile';
  }
  switch (pathname) {
    case '/': 
      return 'Dashboard';
    case '/sessions':
    case '/sessions/active': 
      return 'Live Sessions';
    case '/sessions/completed': 
      return 'Complete';
    case '/configuration': 
      return 'Templates';
    case '/api': 
      return 'Developer API';
    case '/settings': 
      return 'Workspace Settings';
    default: 
      return 'Dashboard';
  }
};

export default function RootLayout() {
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isTourEnabled, setTourEnabled } = useTourStore();

  return (
     <div className="flex bg-[#1d1e28] h-screen overflow-hidden">
      <Sidebar
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />
      <main
        className={cn(
          "w-full bg-[#0a0c16] h-screen overflow-auto transition-opacity duration-300",
          "px-4 pb-8 md:px-6 md:py-4",
          isMobileMenuOpen && "overflow-hidden md:overflow-auto"
        )}
      > 
        <div className="flex justify-between items-center pb-2 py-5  md:py-0 md:pb-5 pl-14 sticky md:static top-0 z-40 md:pl-2 border-b border-slate-800 bg-[#0a0c16]">
          <h1 className="text-[22px] md:text-[26px] font-semibold text-white">{pageTitle}</h1>

          {/* Desktop/tablet: full text button (hidden on small screens) */}
          <div className="hidden md:block ml-auto">
            <Button variant="neutral" size="sm" onClick={() => setTourEnabled(!isTourEnabled)}>
              <HelpCircle />
              {isTourEnabled ? 'Hide Guide' : 'Show Guide'}
            </Button>
          </div>

          {/* Mobile: compact label + toggle that visually relates to desktop button */}
          <div className="md:hidden ml-4 flex items-center gap-3">
            <div className="flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-slate-300" />
              <span className="text-sm font-medium text-slate-200">Guide</span>
            </div>

            <button
              role="switch"
              aria-checked={isTourEnabled}
              onClick={() => setTourEnabled(!isTourEnabled)}
              className={cn(
                "relative inline-flex items-center rounded-full transition-colors duration-200",
                isTourEnabled ? "bg-[#535BB8]" : "bg-gray-700/40",
                "w-12 h-6"
              )}
            >
              <span
                className={cn(
                  "block bg-white rounded-full w-5 h-5 transform transition-transform duration-200 shadow",
                  isTourEnabled ? "translate-x-6" : "translate-x-0"
                )}
              />
            </button>
          </div>
        </div>
        <div className="mt-2">
          <Outlet />
        </div>
      </main>
    </div>
  );
}